import React, { useState, useEffect } from 'react';
import styled, { keyframes } from 'styled-components';
import { Lock, Unlock, ChevronRight, Clock, Shield, Trash2 } from 'lucide-react';
import { fontWeight } from '../../styles/theme';
import BackButton from '../components/BackButton';
import { usePinLock } from '../../hooks/usePinLock';
import { useTheme } from '../../contexts/ThemeContext';

const slideDown = keyframes`
  from { opacity: 0; transform: translateX(-50%) translateY(-8px); }
  to   { opacity: 1; transform: translateX(-50%) translateY(0); }
`;

const fadeUp = keyframes`
  from { opacity: 0; transform: translateY(10px); }
  to   { opacity: 1; transform: translateY(0); }
`;

const Toast = styled.div`
  position: fixed;
  top: 16px;
  left: 50%;
  transform: translateX(-50%);
  z-index: 10000;
  padding: 10px 18px;
  border-radius: 100px;
  font-size: 14px;
  font-weight: 600;
  color: #fff;
  white-space: nowrap;
  animation: ${slideDown} 0.25s ease;
  background: ${props => props.$type === 'error' ? '#ef4444' : '#16a34a'};
  box-shadow: 0 4px 20px rgba(0,0,0,0.3);
`;

const Container = styled.div`
  height: 100vh;
  background: ${props => props.theme.colors.background};
  overflow: hidden;
`;

const Content = styled.div`
  height: 100vh;
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
  padding: 0 20px 60px;
  max-width: 480px;
  margin: 0 auto;
  animation: ${fadeUp} 0.3s ease both;
`;

/* ── Hero ── */
const Hero = styled.div`
  padding: 72px 0 32px;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 14px;
`;

const HeroIcon = styled.div`
  width: 72px;
  height: 72px;
  border-radius: 22px;
  background: ${props => props.$active ? 'rgba(249,115,22,0.15)' : props.theme.colors.border};
  border: 1px solid ${props => props.$active ? 'rgba(249,115,22,0.3)' : props.theme.colors.border};
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${props => props.$active ? '#f97316' : props.theme.colors.muted};
  transition: all 0.3s;
`;

const HeroTitle = styled.h1`
  font-size: 26px;
  font-weight: ${fontWeight.bold};
  color: ${props => props.theme.colors.text};
  margin: 0;
  letter-spacing: -0.02em;
`;

const HeroBadge = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 5px 12px;
  border-radius: 100px;
  font-size: 12px;
  font-weight: 600;
  background: ${props => props.$active ? 'rgba(34,197,94,0.1)' : props.theme.colors.border};
  color: ${props => props.$active ? '#22c55e' : props.theme.colors.muted};
  border: 1px solid ${props => props.$active ? 'rgba(34,197,94,0.2)' : props.theme.colors.border};
`;

const Dot = styled.div`
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: currentColor;
`;

/* ── Cards ── */
const Section = styled.div`
  margin-bottom: 20px;
`;

const SectionLabel = styled.div`
  font-size: 11px;
  font-weight: ${fontWeight.semibold};
  color: ${props => props.theme.colors.muted};
  text-transform: uppercase;
  letter-spacing: 0.07em;
  margin-bottom: 8px;
  padding-left: 4px;
  opacity: 0.6;
`;

const Card = styled.div`
  background: ${props => props.theme.colors.surface};
  border-radius: 14px;
  overflow: hidden;
  border: 1px solid ${props => props.theme.mode === 'light' ? props.theme.colors.border : 'transparent'};
`;

const Row = styled.button`
  display: flex;
  align-items: center;
  padding: 14px 16px;
  background: none;
  border: none;
  cursor: ${props => props.$static ? 'default' : 'pointer'};
  width: 100%;
  text-align: left;
  transition: background 0.15s;
  &:not(:last-child) { border-bottom: 1px solid ${props => props.theme.mode === 'light' ? props.theme.colors.border : 'rgba(255,255,255,0.05)'}; }
  &:hover { background: ${props => props.$static ? 'transparent' : props.theme.mode === 'light' ? 'rgba(0,0,0,0.03)' : 'rgba(255,255,255,0.03)'}; }
`;

const IconBox = styled.div`
  width: 32px;
  height: 32px;
  border-radius: 9px;
  background: ${props => props.$color};
  display: flex;
  align-items: center;
  justify-content: center;
  margin-right: 13px;
  flex-shrink: 0;
  color: #fff;
`;

const RowLabel = styled.div`
  flex: 1;
  font-size: 15px;
  font-weight: ${fontWeight.medium};
  color: ${props => props.$destructive ? '#ef4444' : props.theme.colors.text};
  letter-spacing: -0.01em;
`;

const RowValue = styled.div`
  font-size: 13px;
  color: ${props => props.theme.colors.muted};
  margin-right: 6px;
  opacity: 0.5;
`;

const Chevron = styled.div`
  color: ${props => props.theme.colors.muted};
  opacity: 0.25;
`;

/* ── Confirm modal ── */
const Overlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0,0,0,0.55);
  backdrop-filter: blur(12px);
  -webkit-backdrop-filter: blur(12px);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 10000;
`;

const Modal = styled.div`
  background: ${props => props.theme.colors.surface};
  border-radius: 18px;
  max-width: 380px;
  width: 90%;
  padding: 24px 20px 20px;
  box-shadow: 0 32px 80px rgba(0,0,0,0.4);
  border: 1px solid ${props => props.theme.colors.border};
`;

const ModalTitle = styled.h2`
  margin: 0 0 8px;
  font-size: 18px;
  font-weight: ${fontWeight.bold};
  color: ${props => props.theme.colors.text};
`;

const ModalText = styled.p`
  margin: 0 0 20px;
  font-size: 15px;
  color: ${props => props.theme.colors.muted};
`;

const ModalButtons = styled.div`
  display: flex;
  gap: 10px;
`;

const ModalBtn = styled.button`
  border: none;
  border-radius: 999px;
  padding: 9px 20px;
  font-weight: ${fontWeight.semibold};
  cursor: pointer;
  font-size: 14px;
  background: ${props => props.$danger ? '#ef4444' : props.theme.colors.border};
  color: ${props => props.$danger ? '#fff' : props.theme.colors.text};
  transition: filter 0.15s;
  &:hover { filter: brightness(0.92); }
`;

/* ── Timeout picker ── */
const PickerOption = styled.button`
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  padding: 13px 14px;
  background: none;
  border: none;
  border-radius: 0;
  cursor: pointer;
  color: ${props => props.$selected ? '#f97316' : props.theme.colors.text};
  font-size: 15px;
  font-weight: ${props => props.$selected ? fontWeight.semibold : fontWeight.medium};
  transition: background 0.15s;
  &:not(:last-child) { border-bottom: 1px solid ${props => props.theme.mode === 'light' ? props.theme.colors.border : 'rgba(255,255,255,0.05)'}; }
  &:hover { background: ${props => props.theme.mode === 'light' ? 'rgba(0,0,0,0.03)' : 'rgba(255,255,255,0.03)'}; }
`;

const CheckMark = styled.div`
  width: 20px;
  height: 20px;
  border-radius: 50%;
  border: 2px solid ${props => props.$selected ? '#f97316' : props.theme.colors.border};
  background: ${props => props.$selected ? '#f97316' : 'transparent'};
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 11px;
  color: #fff;
  flex-shrink: 0;
`;

/* ── Numpad view ── */
const NumpadWrap = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  padding-top: 16px;
  gap: 24px;
`;

const StepLabel = styled.div`
  font-size: 14px;
  color: ${props => props.theme.colors.muted};
  font-weight: ${fontWeight.medium};
`;

const PinDots = styled.div`
  display: flex;
  gap: 14px;
`;

const PinDot = styled.div`
  width: 14px;
  height: 14px;
  border-radius: 50%;
  background: ${props => props.$filled ? '#f97316' : 'transparent'};
  border: 2px solid ${props => props.$filled ? '#f97316' : props.theme.colors.border};
  transition: all 0.15s;
`;

const Numpad = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 72px);
  grid-template-rows: repeat(4, 72px);
  gap: 10px;
`;

const Key = styled.button`
  background: ${props => props.$ghost ? 'transparent' : props.theme.mode === 'light' ? 'rgba(0,0,0,0.06)' : 'rgba(255,255,255,0.06)'};
  border: ${props => props.$ghost ? 'none' : `1px solid ${props.theme.colors.border}`};
  border-radius: 50%;
  color: ${props => props.$ghost ? props.theme.colors.muted : props.theme.colors.text};
  font-size: ${props => props.$del ? '20px' : '22px'};
  font-weight: 600;
  cursor: pointer;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 2px;
  transition: background 0.1s, transform 0.1s;
  &:active { background: ${props => props.theme.mode === 'light' ? 'rgba(0,0,0,0.12)' : 'rgba(255,255,255,0.14)'}; transform: scale(0.92); }
`;

const KeySub = styled.div`
  font-size: 9px;
  font-weight: 500;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  opacity: 0.35;
`;

const CancelBtn = styled.button`
  background: none;
  border: none;
  color: ${props => props.theme.colors.muted};
  font-size: 14px;
  cursor: pointer;
  padding: 8px 16px;
  &:hover { color: ${props => props.theme.colors.text}; }
`;

const KEYS = [
  { d: '1', s: '' }, { d: '2', s: 'ABC' }, { d: '3', s: 'DEF' },
  { d: '4', s: 'GHI' }, { d: '5', s: 'JKL' }, { d: '6', s: 'MNO' },
  { d: '7', s: 'PQRS' }, { d: '8', s: 'TUV' }, { d: '9', s: 'WXYZ' },
  { d: null }, { d: '0', s: '' }, { d: 'del' },
];

const TIMEOUT_OPTIONS = [
  { label: '1 minuut', ms: 1 * 60 * 1000 },
  { label: '5 minuten', ms: 5 * 60 * 1000 },
  { label: '15 minuten', ms: 15 * 60 * 1000 },
  { label: '30 minuten', ms: 30 * 60 * 1000 },
  { label: '1 uur', ms: 60 * 60 * 1000 },
  { label: '1,5 uur', ms: 90 * 60 * 1000 },
];

export default function PinSettingsScreen() {
  const { hasPin, setPin, removePin, verifyPin, inactivityMs, setInactivity } = usePinLock();
  const { themeMode } = useTheme();
  const mutedColor = themeMode === 'light' ? 'rgba(0,0,0,0.35)' : 'rgba(255,255,255,0.35)';

  const [step, setStep] = useState(null); // null | 'enter' | 'confirm'
  const [pin1, setPin1] = useState('');
  const [pin2, setPin2] = useState('');
  const [showPicker, setShowPicker] = useState(false);
  const [showRemovePin, setShowRemovePin] = useState(false);
  const [removePin2, setRemovePin2] = useState('');
  const [showVerifyPin, setShowVerifyPin] = useState(false);
  const [verifyPin2, setVerifyPin2] = useState('');
  const [toast, setToast] = useState(null);

  const showToast = (msg, type = 'success') => {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 2500);
  };

  const currentPin = step === 'enter' ? pin1 : pin2;
  const setCurrentPin = step === 'enter' ? setPin1 : setPin2;

  const press = (digit) => {
    if (currentPin.length >= 6) return;
    const next = currentPin + digit;
    setCurrentPin(next);
    if (next.length === 6) {
      if (step === 'enter') {
        setTimeout(() => setStep('confirm'), 150);
      } else {
        setTimeout(() => confirmPin(pin1, next), 150);
      }
    }
  };

  const del = () => setCurrentPin(p => p.slice(0, -1));

  useEffect(() => {
    if (!step) return;
    const handle = (e) => {
      if (e.key >= '0' && e.key <= '9') press(e.key);
      if (e.key === 'Backspace') del();
    };
    window.addEventListener('keydown', handle);
    return () => window.removeEventListener('keydown', handle);
  }, [step, currentPin]);

  useEffect(() => {
    if (!showRemovePin) return;
    const handle = (e) => {
      if (e.key >= '0' && e.key <= '9') pressRemove(e.key);
      if (e.key === 'Backspace') setRemovePin2(p => p.slice(0, -1));
    };
    window.addEventListener('keydown', handle);
    return () => window.removeEventListener('keydown', handle);
  }, [showRemovePin, removePin2]);

  useEffect(() => {
    if (!showVerifyPin) return;
    const handle = (e) => {
      if (e.key >= '0' && e.key <= '9') pressVerify(e.key);
      if (e.key === 'Backspace') setVerifyPin2(p => p.slice(0, -1));
    };
    window.addEventListener('keydown', handle);
    return () => window.removeEventListener('keydown', handle);
  }, [showVerifyPin, verifyPin2]);

  const confirmPin = async (p1, p2) => {
    if (p1 !== p2) {
      showToast('Pincodes komen niet overeen', 'error');
      setPin1(''); setPin2(''); setStep('enter');
      return;
    }
    const ok = await setPin(p1);
    if (ok) {
      showToast('Pincode ingesteld');
    } else {
      showToast('Er ging iets mis', 'error');
    }
    setStep(null); setPin1(''); setPin2('');
  };

  const handleRemove = async () => {
    const ok = await removePin();
    if (ok) showToast('Pincode verwijderd');
    else showToast('Er ging iets mis', 'error');
  };

  const pressRemove = (digit) => {
    if (removePin2.length >= 6) return;
    const next = removePin2 + digit;
    setRemovePin2(next);
    if (next.length === 6) {
      setTimeout(async () => {
        const verified = await verifyPin(next);
        if (verified) {
          setShowRemovePin(false);
          setRemovePin2('');
          handleRemove();
        } else {
          showToast('Onjuiste pincode', 'error');
          setRemovePin2('');
        }
      }, 150);
    }
  };

  const startSetup = () => { setStep('enter'); setPin1(''); setPin2(''); setToast(null); };
  const startChange = () => { setShowVerifyPin(true); setVerifyPin2(''); setToast(null); };

  const pressVerify = (digit) => {
    if (verifyPin2.length >= 6) return;
    const next = verifyPin2 + digit;
    setVerifyPin2(next);
    if (next.length === 6) {
      setTimeout(async () => {
        const ok = await verifyPin(next);
        if (ok) {
          setShowVerifyPin(false);
          setVerifyPin2('');
          startSetup();
        } else {
          showToast('Onjuiste pincode', 'error');
          setVerifyPin2('');
        }
      }, 150);
    }
  };
  const cancel = () => { setStep(null); setPin1(''); setPin2(''); };

  const currentTimeout = TIMEOUT_OPTIONS.find(o => o.ms === inactivityMs) || TIMEOUT_OPTIONS[1];

  return (
    <Container>
      {toast && <Toast $type={toast.type}>{toast.msg}</Toast>}

      <Content>

        <Hero>
          <HeroIcon $active={hasPin}>
            {hasPin ? <Lock size={30} /> : <Unlock size={30} />}
          </HeroIcon>
          <HeroTitle>Pincode</HeroTitle>
          <HeroBadge $active={hasPin}>
            <Dot />
            {hasPin ? 'Actief' : 'Niet ingesteld'}
          </HeroBadge>
        </Hero>

        {!step && (
          <>
            <Section>
              <SectionLabel>Beveiliging</SectionLabel>
              <Card>
                {hasPin ? (
                  <>
                    <Row onClick={startChange}>
                      <IconBox $color="rgba(249,115,22,0.8)"><Shield size={15} /></IconBox>
                      <RowLabel>Pincode wijzigen</RowLabel>
                      <Chevron><ChevronRight size={16} /></Chevron>
                    </Row>
                    <Row onClick={() => setShowPicker(true)}>
                      <IconBox $color="rgba(99,102,241,0.8)"><Clock size={15} /></IconBox>
                      <div style={{ flex: 1 }}>
                        <RowLabel>Automatisch vergrendelen</RowLabel>
                        <div style={{ fontSize: 12, color: mutedColor, marginTop: 2 }}>
                          App vergrendelt na {currentTimeout.label} inactiviteit
                        </div>
                      </div>
                      <RowValue>{currentTimeout.label}</RowValue>
                      <Chevron><ChevronRight size={16} /></Chevron>
                    </Row>
                    <Row onClick={() => { setShowRemovePin(true); setRemovePin2(''); }}>
                      <IconBox $color="rgba(239,68,68,0.7)"><Trash2 size={15} /></IconBox>
                      <RowLabel $destructive>Pincode verwijderen</RowLabel>
                    </Row>
                  </>
                ) : (
                  <Row onClick={startSetup}>
                    <IconBox $color="rgba(249,115,22,0.8)"><Shield size={15} /></IconBox>
                    <RowLabel>Pincode instellen</RowLabel>
                    <Chevron><ChevronRight size={16} /></Chevron>
                  </Row>
                )}
              </Card>
            </Section>
          </>
        )}
      </Content>

      {step && (
        <Overlay onClick={cancel}>
          <Modal onClick={e => e.stopPropagation()}>
            <ModalTitle style={{ textAlign: 'center' }}>{step === 'enter' ? 'Nieuwe pincode' : 'Bevestig pincode'}</ModalTitle>
            <ModalText style={{ textAlign: 'center' }}>{step === 'enter' ? 'Kies een 6-cijferige pincode.' : 'Voer je pincode nog een keer in.'}</ModalText>
            <PinDots style={{ justifyContent: 'center', marginBottom: 20 }}>
              {Array.from({ length: 6 }).map((_, i) => (
                <PinDot key={i} $filled={currentPin.length > i} />
              ))}
            </PinDots>
            <Numpad style={{ margin: '0 auto', width: 'fit-content' }}>
              {KEYS.map(({ d, s }, i) =>
                d === null ? <div key={i} /> :
                d === 'del' ? (
                  <Key key={i} $ghost $del onClick={del}>⌫</Key>
                ) : (
                  <Key key={i} onClick={() => press(d)}>
                    {d}
                    {s && <KeySub>{s}</KeySub>}
                  </Key>
                )
              )}
            </Numpad>
            <ModalButtons style={{ marginTop: 20, justifyContent: 'center' }}>
              <ModalBtn onClick={cancel}>Annuleren</ModalBtn>
            </ModalButtons>
          </Modal>
        </Overlay>
      )}

      {showVerifyPin && (
        <Overlay onClick={() => { setShowVerifyPin(false); setVerifyPin2(''); }}>
          <Modal onClick={e => e.stopPropagation()}>
            <ModalTitle style={{ textAlign: 'center' }}>Huidige pincode</ModalTitle>
            <ModalText style={{ textAlign: 'center' }}>Voer je huidige pincode in om door te gaan.</ModalText>
            <PinDots style={{ justifyContent: 'center', marginBottom: 20 }}>
              {Array.from({ length: 6 }).map((_, i) => (
                <PinDot key={i} $filled={verifyPin2.length > i} />
              ))}
            </PinDots>
            <Numpad style={{ margin: '0 auto', width: 'fit-content' }}>
              {KEYS.map(({ d, s }, i) =>
                d === null ? <div key={i} /> :
                d === 'del' ? (
                  <Key key={i} $ghost $del onClick={() => setVerifyPin2(p => p.slice(0, -1))}>⌫</Key>
                ) : (
                  <Key key={i} onClick={() => pressVerify(d)}>
                    {d}
                    {s && <KeySub>{s}</KeySub>}
                  </Key>
                )
              )}
            </Numpad>
            <ModalButtons style={{ marginTop: 20, justifyContent: 'center' }}>
              <ModalBtn onClick={() => { setShowVerifyPin(false); setVerifyPin2(''); }}>Annuleren</ModalBtn>
            </ModalButtons>
          </Modal>
        </Overlay>
      )}

      {showRemovePin && (
        <Overlay onClick={() => { setShowRemovePin(false); setRemovePin2(''); }}>
          <Modal onClick={e => e.stopPropagation()}>
            <ModalTitle style={{ textAlign: 'center' }}>Pincode verwijderen</ModalTitle>
            <ModalText style={{ textAlign: 'center' }}>Voer je huidige pincode in om te bevestigen.</ModalText>
            <PinDots style={{ justifyContent: 'center', marginBottom: 20 }}>
              {Array.from({ length: 6 }).map((_, i) => (
                <PinDot key={i} $filled={removePin2.length > i} />
              ))}
            </PinDots>
            <Numpad style={{ margin: '0 auto', width: 'fit-content' }}>
              {KEYS.map(({ d, s }, i) =>
                d === null ? <div key={i} /> :
                d === 'del' ? (
                  <Key key={i} $ghost $del onClick={() => setRemovePin2(p => p.slice(0, -1))}>⌫</Key>
                ) : (
                  <Key key={i} onClick={() => pressRemove(d)}>
                    {d}
                    {s && <KeySub>{s}</KeySub>}
                  </Key>
                )
              )}
            </Numpad>
            <ModalButtons style={{ marginTop: 20, justifyContent: 'center' }}>
              <ModalBtn onClick={() => { setShowRemovePin(false); setRemovePin2(''); }}>Annuleren</ModalBtn>
            </ModalButtons>
          </Modal>
        </Overlay>
      )}

      {showPicker && (
        <Overlay onClick={() => setShowPicker(false)}>
          <Modal onClick={e => e.stopPropagation()}>
            <ModalTitle style={{ textAlign: 'center', marginBottom: 4 }}>Automatisch vergrendelen</ModalTitle>
            <ModalText style={{ textAlign: 'center' }}>App vergrendelt na inactiviteit van:</ModalText>
            {TIMEOUT_OPTIONS.map(opt => (
              <PickerOption
                key={opt.ms}
                $selected={inactivityMs === opt.ms}
                onClick={() => { setInactivity(opt.ms); setShowPicker(false); }}
              >
                {opt.label}
                <CheckMark $selected={inactivityMs === opt.ms}>
                  {inactivityMs === opt.ms && '✓'}
                </CheckMark>
              </PickerOption>
            ))}
          </Modal>
        </Overlay>
      )}
    </Container>
  );
}
